// Deployments — ship this project as a STANDALONE app, separate from the
// cortex platform: its own namespace, its own domain, per-deployment image
// history. Presented with the same Drive-style idiom as Apps/Agents/Audit:
// a collapsible left rail of deployments (per-project numbering from the
// engine's stored seq), a detail pane, and the primary action ("Deploy
// project") in the rail footer.
//
// Engine side: the Flow exporter compiles the fork (flattened endpoints incl.
// the workflow engine + pipelines, service containers, app views, endpoints
// dashboard) into a git commit, kaniko-builds images tagged with that commit,
// and deploys them behind an ALB at <name>-<id4>.tiva-ai.com. This page talks
// to the thin /api/deployments proxy.
import React, { useCallback, useEffect, useState } from "react";
import { useProject } from "../contexts/ProjectContext";
import { fetchData } from "../fetchapi/fetchapi";
import {
  Rocket, Play, Loader2, RefreshCw, CircleCheck, CircleX, ExternalLink,
  PanelLeftClose, PanelLeftOpen, Gauge, History, Package, Globe,
  X, Plus, Lock, LockOpen, Settings2, Eye, PowerOff, AlertTriangle, Database, Pencil,
  Download, HardDriveDownload, Terminal, Boxes, Server, Save,
} from "lucide-react";
import { btnPrimary, btnGhost, fieldCls, sectionLabel } from "../components/board/requestMeta";

const api = (path, body) =>
  fetchData(`deployments/${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

const fmt = (v) => { try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return String(v || ""); } };
const shortHash = (h) => (h ? String(h).slice(0, 8) : "—");

// A torn-down deployment is neither healthy nor broken — it is simply GONE, so
// it gets its own muted rendering rather than borrowing the green "live" look
// StatusWord/StatusIcon default to.
const isShutDown = (status) => status === "shut_down";

function StatusIcon({ status, size = 15 }) {
  if (status === "running") return <Loader2 size={size} className="shrink-0 animate-spin" style={{ color: "#d97706" }} />;
  if (status === "failed") return <CircleX size={size} className="shrink-0" style={{ color: "#dc2626" }} />;
  if (isShutDown(status)) return <PowerOff size={size} className="shrink-0" style={{ color: "#9ca3af" }} />;
  return <CircleCheck size={size} className="shrink-0" style={{ color: "#16a34a" }} />;
}

function StatusWord({ status }) {
  if (isShutDown(status)) return <b style={{ color: "#6b7280" }} data-testid="deployment-status-shut-down">shut down</b>;
  const color = status === "running" ? "#d97706" : status === "failed" ? "#dc2626" : "#16a34a";
  return <b style={{ color }}>{status || "live"}</b>;
}

// Client-side mirror of the backend's subdomain validation (backend/modules/
// deployments parseDeployOptions) — the server stays authoritative, this only
// gives instant feedback in the modal.
const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const RESERVED_SUBDOMAINS = new Set(["www", "api", "mail", "accounts", "exporter", "auth", "admin"]);
const subdomainProblem = (name) => {
  if (!SUBDOMAIN_RE.test(name)) return "Use lowercase letters, digits and hyphens (not at the edges).";
  if (RESERVED_SUBDOMAINS.has(name) || name === "cortex" || name.startsWith("cortex-") || name.startsWith("flow-"))
    return `“${name}” is reserved for the platform.`;
  return null;
};

// The deploy-settings modal: "Deploy project" no longer fires immediately —
// the user first chooses whether the app requires sign-in and which custom
// subdomains (under tiva-ai.com) the app views should also answer on. The
// choices ride on the deploy call and are recorded on the deployment's
// history row, so each deployment's detail view shows what it shipped with.
//
// The modal offers TWO destinations for the same compiled project:
//   Deploy project            → the cluster (namespace, domains, ALB)
//   Download for local run    → a tar.gz the user runs with docker compose
// They share the export and the Data choice; everything else (sign-in, custom
// subdomains) is cluster-only and is dimmed to say so when the local
// destination is selected, rather than hidden — a control that vanishes reads
// as a bug, one that explains itself teaches the difference.
function DeployOptionsModal({ defaults, knownModules, busy, onClose, onDeploy, onPackage, deliveryLine }) {
  // "cluster" | "local" — which button is primary, and which options apply.
  const [destination, setDestination] = useState("cluster");
  const isLocal = destination === "local";
  const [authEnabled, setAuthEnabled] = useState(defaults?.authEnabled !== false);
  // Deliberately NOT prefilled from the last deployment (unlike the other
  // options): re-seeding erases the deployment's data, so it is re-chosen
  // every time rather than silently repeated because it was ticked once.
  const [seedData, setSeedData] = useState(false);
  // CUSTOMER ARCHIVE (local destination only): what a customer deploys
  // themselves — only what the apps use, no platform people or credentials,
  // every variable documented, the gaps section in the runbook. Off by
  // default: the developer bundle is the full project as before.
  const [customer, setCustomer] = useState(false);
  const [rows, setRows] = useState(() =>
    (defaults?.subdomains || []).map((s) => ({ name: s.name, module: s.module || "" })));
  const [input, setInput] = useState("");
  const [inputErr, setInputErr] = useState("");

  const addSubdomain = () => {
    const name = input.trim().toLowerCase();
    if (!name) return;
    const problem = subdomainProblem(name);
    if (problem) { setInputErr(problem); return; }
    if (rows.some((r) => r.name === name)) { setInputErr("Already in the list."); return; }
    if (rows.length >= 10) { setInputErr("At most 10 custom subdomains."); return; }
    setRows((r) => [...r, { name, module: knownModules[0] || "" }]);
    setInput(""); setInputErr("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="deploy-options-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5"
        style={{ width: 560, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh", overflowY: "auto" }}>
        <div className="flex items-start justify-between mb-1">
          <div className="font-semibold text-page-text inline-flex items-center gap-2"><Rocket size={16} /> Deploy project</div>
          <button type="button" onClick={onClose} data-testid="deploy-options-close"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <X size={16} />
          </button>
        </div>
        <div className="text-sm text-page-muted mb-4">
          Choose where this build goes. The settings are recorded on the deployment.
        </div>

        <div className="mb-4">
          <div className={sectionLabel + " mb-1.5"}>Destination</div>
          <div className="grid grid-cols-2 gap-2" data-testid="deploy-options-destination">
            {[
              { key: "cluster", icon: <Rocket size={14} />, title: "Deploy to the cluster",
                sub: "Its own infrastructure and domain, reachable from anywhere." },
              { key: "local", icon: <HardDriveDownload size={14} />, title: "Download for local run",
                sub: "An archive with every image's build and a docker-compose file." },
            ].map((opt) => (
              <button key={opt.key} type="button" data-testid={`deploy-options-dest-${opt.key}`}
                onClick={() => setDestination(opt.key)}
                className={
                  "text-left rounded-md border p-2.5 cursor-pointer " +
                  (destination === opt.key
                    ? "border-nav-active bg-nav-active/10 text-page-text"
                    : "border-card-border bg-transparent text-page-text hover:bg-card-border/30")
                }>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium">{opt.icon} {opt.title}</span>
                <span className="block text-xs text-page-muted mt-1">{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Access applies to BOTH destinations. A local bundle carries its own
            sign-in authority (the standalone credentials in its README) instead
            of the cortex accounts service, so the same choice is honest there:
            gate every page, or serve the app pages directly the way a public
            cluster deploy does (the bundle's portal stays sign-in only). */}
        <div className="mb-4">
          <div className={sectionLabel + " mb-1.5"}>Access</div>
          <label className="flex items-start gap-2.5 select-none cursor-pointer">
            <input type="checkbox" checked={authEnabled} data-testid="deploy-options-auth"
              onChange={(e) => setAuthEnabled(e.target.checked)} className="mt-0.5 cursor-pointer" />
            <span className="text-sm text-page-text">
              Require sign-in (login page)
              <span className="block text-xs text-page-muted mt-0.5">
                {isLocal
                  ? (authEnabled
                    ? "Every page asks for the bundle's local credentials (printed in its README) — the archive cannot reach cortex accounts, so it carries its own sign-in."
                    : "App pages open directly, like a public cluster deploy — an app with its own sign-in still shows it. Keep the ports on localhost.")
                  : authEnabled
                    ? "Users must log in to open the app. Access is checked live against cortex accounts."
                    : "The app pages will be PUBLIC — anyone with the link can open them and use their data. The admin portal and endpoints dashboard stay sign-in only."}
              </span>
            </span>
          </label>
        </div>

        <div className="mb-4">
          <div className={sectionLabel + " mb-1.5"}>Data</div>
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={seedData} data-testid="deploy-options-seed"
              onChange={(e) => setSeedData(e.target.checked)} className="mt-0.5 cursor-pointer" />
            <span className="text-sm text-page-text">
              Reset data from cortex
              <span className="block text-xs text-page-muted mt-0.5"
                style={seedData && !isLocal ? { color: "#dc2626" } : undefined}>
                {isLocal
                  ? (seedData
                    ? "The archive's data is loaded on every start, overwriting whatever the local stack accumulated."
                    : "The archive carries a copy of this project's data; the local stack keeps whatever it already has.")
                  : seedData
                    ? "Overwrites ALL of this deployment's data with a fresh copy from cortex. Everything users did in the deployed app is erased."
                    : "The deployment keeps its own data; only missing databases are seeded from cortex."}
              </span>
            </span>
          </label>
        </div>

        {isLocal ? (
          <div className="mb-5 rounded-md border border-page-divider p-3" data-testid="deploy-options-local-note">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input type="checkbox" checked={customer} data-testid="deploy-options-customer"
                onChange={(e) => setCustomer(e.target.checked)} className="mt-0.5 cursor-pointer" />
              <span className="text-sm text-page-text">
                Customer archive — the delivery a customer deploys themselves
                <span className="block text-xs text-page-muted mt-0.5">
                  Only what the delivered apps use (the app services; the engine and companion pages only when
                  a speed-dial companion is on), no platform accounts or credentials, every variable documented
                  with its description, the development stand-ins in a separate compose file, and the runbook's
                  "not in this delivery" section from the requirements.
                </span>
              </span>
            </label>
            <div className="text-sm text-page-text inline-flex items-center gap-1.5 font-medium mt-3">
              <Terminal size={14} /> What you get
            </div>
            <div className="text-xs text-page-muted mt-1.5">
              {customer ? (
                <>A <span className="font-mono">.tar.gz</span> with the app services' source and Dockerfiles, a{" "}
                <span className="font-mono">docker-compose.yml</span>, the documented{" "}
                <span className="font-mono">.env</span> files, <span className="font-mono">docs/RUNBOOK.md</span>, the
                project's Knowledge pages, and a <span className="font-mono">run.sh</span>. Packaging takes a few
                minutes; the archive appears in this list to download.</>
              ) : (
                <>A <span className="font-mono">.tar.gz</span> with every component's Docker build
                (the endpoints runtime, each service, the app pages, the dashboard), a{" "}
                <span className="font-mono">docker-compose.yml</span> wiring them to their own
                postgres and dolt, this project's data, and a{" "}
                <span className="font-mono">run.sh</span> that builds and starts it all. Packaging
                takes a few minutes; the archive appears in this list to download.</>
              )}
            </div>
            {/* Said here, at the moment of the decision — not only in the
                archive's README, which is read after the file already exists.
                The developer bundle has to carry the app's real env to run at
                all, and that env is where the credentials are. */}
            {customer ? (
              <div className="text-xs mt-2 inline-flex items-start gap-1.5 text-page-muted" data-testid="deploy-options-customer-note">
                <Boxes size={13} className="shrink-0 mt-0.5" />
                <span>
                  Every credential inside is generated for the archive; the platform's own secrets, accounts and
                  hostnames stay out. Values only the customer knows are left blank and listed in the README.
                </span>
              </div>
            ) : (
              <div className="text-xs mt-2 inline-flex items-start gap-1.5" style={{ color: "#b45309" }}>
                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                <span>
                  The archive contains this project's data and the credentials its app runs with.
                  Treat it like a production database dump.
                </span>
              </div>
            )}
          </div>
        ) : null}

        {/* What the export DELIVERS (Delivery options on the overview): app
            services, deployment descriptors, CI, runbook. Shown for both
            destinations — the same export commit feeds them. */}
        {deliveryLine ? (
          <div className="mb-5 text-xs text-page-muted inline-flex items-start gap-1.5" data-testid="deploy-options-delivery-line">
            <Boxes size={13} className="shrink-0 mt-0.5" />
            <span>Delivery: {deliveryLine} — change it under <b>Delivery</b> on the Deployments overview.</span>
          </div>
        ) : null}

        <div className={"mb-5" + (isLocal ? " opacity-50" : "")}>
          <div className={sectionLabel + " mb-1.5"}>Custom subdomains</div>
          <div className="text-xs text-page-muted mb-2">
            {isLocal
              ? "Cluster deployments only — a local run answers on localhost."
              : <>Extra addresses under <span className="font-mono">tiva-ai.com</span> where the app
                (its views) is also published. The generated domains keep working either way.</>}
          </div>
          <div className="flex items-center gap-2">
            <input value={input} data-testid="deploy-options-subdomain-input" placeholder="e.g. my-app" disabled={isLocal}
              onChange={(e) => { setInput(e.target.value); setInputErr(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubdomain(); } }}
              className={fieldCls + " flex-1"} />
            <span className="text-sm text-page-muted font-mono shrink-0">.tiva-ai.com</span>
            <button type="button" onClick={addSubdomain} data-testid="deploy-options-add-subdomain" className={btnGhost} disabled={isLocal}>
              <Plus size={14} /> Add
            </button>
          </div>
          {inputErr ? <div className="text-xs mt-1" style={{ color: "#dc2626" }} data-testid="deploy-options-subdomain-error">{inputErr}</div> : null}
          {rows.length > 0 ? (
            <div className="mt-2 border border-page-divider rounded-md px-3 py-1">
              {rows.map((r, i) => (
                <div key={r.name} className={"flex items-center gap-2 text-sm py-1.5 " + (i ? "border-t border-page-divider" : "")}
                  data-testid={`deploy-options-subdomain-${r.name}`}>
                  <Globe size={13} className="shrink-0 text-page-muted" />
                  <span className="font-mono flex-1 truncate">{r.name}.tiva-ai.com</span>
                  {knownModules.length > 1 ? (
                    <select value={r.module || knownModules[0]} title="Which app answers on this subdomain"
                      data-testid={`deploy-options-module-${r.name}`}
                      onChange={(e) => setRows((rows) => rows.map((x) => x.name === r.name ? { ...x, module: e.target.value } : x))}
                      className={fieldCls + " py-1 text-xs"}>
                      {knownModules.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  ) : null}
                  <button type="button" title="Remove" onClick={() => setRows((rows) => rows.filter((x) => x.name !== r.name))}
                    className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-0.5">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost} data-testid="deploy-options-cancel">Cancel</button>
          {isLocal ? (
            // Only the Data choice reaches the engine here: sign-in and
            // subdomains have no meaning off-cluster, so they are not sent
            // rather than sent and ignored.
            <button type="button" disabled={busy} data-testid="deploy-options-package" className={btnPrimary}
              onClick={() => onPackage({ seedData, authEnabled, customer })}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <HardDriveDownload size={14} />} {customer ? "Build the customer archive" : "Build the archive"}
            </button>
          ) : (
            <button type="button" disabled={busy} data-testid="deploy-options-confirm" className={btnPrimary}
              onClick={() => onDeploy({
                authEnabled,
                seedData,
                subdomains: rows.map((r) => (r.module ? { name: r.name, module: r.module } : { name: r.name })),
              })}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Deploy project
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Per-app favicon + humanized tab title ───────────────────────────────────
// A deployed app's page used to show the raw module slug in the browser tab
// ("case-study-factory") with the browser's globe placeholder for an icon. The
// exporter now bakes a humanized <title> and a GENERATED favicon into every
// view page; the icon is stored per module on the fork so it survives
// redeploys, and this is where a user sees it and spends a new variant.
//
// REGENERATION SEMANTICS: the icon is baked at BUILD time, so a regenerate
// updates what the NEXT deploy will ship — the running app keeps the icon in
// its current image. The button says so; making it live would mean serving the
// icon dynamically from every deployed app, which is new runtime plumbing for
// a decoration.
const humanTitle = (name) =>
  String(name || "").split(/[-_\s]+/).filter(Boolean)
    .map((w) => (/[A-Z]/.test(w.slice(1)) || w === w.toUpperCase() ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ") || String(name || "");

// The stored SVG, rendered inline via a data: URI. `img` (not dangerouslySet-
// InnerHTML): the markup comes from the engine, and an <img src="data:…svg">
// cannot run script or reach the page around it.
function FaviconPreview({ svg, module: mod, size = 18 }) {
  if (!svg) {
    return (
      <span data-testid={`deployment-favicon-none-${mod}`} title="No icon recorded yet — the next deploy generates one"
        className="inline-block shrink-0 rounded border border-dashed border-page-divider"
        style={{ width: size, height: size }} />
    );
  }
  return (
    <img data-testid={`deployment-favicon-${mod}`} alt={`${humanTitle(mod)} icon`} width={size} height={size}
      className="inline-block shrink-0 rounded" style={{ width: size, height: size }}
      src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} />
  );
}

// The per-app regenerate control. One click = one new variant (the engine bumps
// the stored salt), so a user can keep clicking until they like the mark.
function RegenerateFaviconButton({ module: mod, busy, onRegenerate }) {
  return (
    <button type="button" onClick={() => onRegenerate(mod)} disabled={busy}
      data-testid={`deployment-favicon-regen-${mod}`}
      title="Draw a new icon for this app — applies on next deploy"
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
      {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Regenerate favicon
    </button>
  );
}

// The app's display title, click-to-edit. The title drives both the deployed
// page's <title> and the favicon's initials, so a save also redraws the icon;
// like the icon, it is baked at build time and applies on the next deploy.
function EditableAppTitle({ module: mod, title, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  useEffect(() => { if (!editing) setValue(title); }, [title, editing]);
  const commit = () => {
    setEditing(false);
    const next = String(value || "").trim();
    if (next !== String(title || "").trim()) onSave(mod, next);
  };
  if (editing) {
    return (
      <input autoFocus value={value} data-testid={`deployment-app-title-input-${mod}`}
        onChange={(e) => setValue(e.target.value)} maxLength={80}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setValue(title); setEditing(false); }
        }}
        className="text-sm bg-page-bg border border-page-divider rounded px-1.5 py-0.5 min-w-0 w-44" />
    );
  }
  return (
    <button type="button" onClick={() => !busy && setEditing(true)} disabled={busy}
      data-testid={`deployment-app-title-${mod}`}
      title={`${mod} — click to rename (title + icon apply on next deploy)`}
      className="inline-flex items-center gap-1 min-w-0 bg-transparent border-0 p-0 cursor-pointer text-page-text hover:text-nav-active">
      <span className="truncate">{title}</span>
      <Pencil size={11} className="shrink-0 opacity-50" />
    </button>
  );
}

// Viewer options as recorded ON the deployment (backend snapshots the apps'
// share-page curation at deploy time; the engine stores it verbatim inside the
// history row's options). Display order + labels mirror the Viewer options
// dialog in Apps.jsx so the two read as the same thing.
const COMPANION_LABELS = [
  ["chat", "Chat"],
  ["workflows", "Workflows"],
  ["guides", "Guides"],
  ["pipeline", "Pipeline"],
  ["governance", "Governance"],
  ["docs", "Docs"],
];
// Older records carry audience strings ('everyone'/'org'); newer ones plain
// booleans — one audience now: every enabled surface, for signed-in viewers.
const companionValueLabel = (v) => (v === true ? "" : v === "everyone" ? "" : v === "org" ? "" : String(v || ""));

// READ-ONLY: a historical record, not a control. Nothing here is clickable —
// editing viewer options happens on the app itself (Apps → ⋯ → Viewer options),
// and doing so must NOT rewrite what an already-shipped deployment shows.
function ViewerOptionsSection({ apps }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Eye size={14} /> Viewer options</h2>
      <div className="border border-page-divider rounded-md px-3 py-1" data-testid="deployment-viewer-options">
        {/* Pre-snapshot deployments (every row that shipped before this was
            recorded) say so plainly — inventing the defaults for them would
            claim a fact we do not have. */}
        {!Array.isArray(apps) || apps.length === 0 ? (
          <div className="text-sm text-page-muted py-1.5" data-testid="deployment-viewer-options-none">
            not recorded for this deployment
          </div>
        ) : apps.map((a, i) => {
          const on = COMPANION_LABELS.filter(([k]) => a?.companions?.[k]);
          return (
            <div key={a.view || i} className={"flex items-start gap-2 text-sm py-1.5" + (i ? " border-t border-page-divider" : "")}
              data-testid={`deployment-viewer-options-app-${a.view || i}`}>
              <span className="font-medium w-56 shrink-0 truncate" title={a.view || ""}>{a.app || a.view || "—"}</span>
              {on.length === 0 ? (
                <span className="text-page-muted">just the app — no companions</span>
              ) : (
                <span className="flex flex-col gap-1 min-w-0">
                  {on.map(([k, label]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 min-w-0">
                      <span className="truncate">{label}</span>
                      <span className="text-xs text-page-muted shrink-0">
                        {companionValueLabel(a.companions[k])}
                      </span>
                    </span>
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// TEARDOWN confirmation. Shutting the live app down deletes real
// infrastructure (namespace → pods/services/ingress → the ALB, the Route53
// CNAMEs, the ECR repos), so the destructive button only unlocks once the
// project name has been TYPED — the same gate the backend enforces on
// /api/deployments/teardown, which is authoritative.
function TeardownModal({ projectSlug, deployment, busy, onClose, onConfirm }) {
  const [typed, setTyped] = useState("");
  const armed = typed.trim() === projectSlug;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="deployment-teardown-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card-bg border border-card-border rounded-xl shadow-xl p-5"
        style={{ width: 560, maxWidth: "calc(100vw - 32px)", maxHeight: "85vh", overflowY: "auto" }}>
        <div className="flex items-start justify-between mb-1">
          <div className="font-semibold inline-flex items-center gap-2" style={{ color: "#b91c1c" }}>
            <AlertTriangle size={16} /> Teardown deployment
          </div>
          <button type="button" onClick={onClose} data-testid="deployment-teardown-close"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <X size={16} />
          </button>
        </div>
        <div className="text-sm text-page-muted mb-3">
          This shuts the live app down. It cannot be undone — bringing it back means deploying again.
        </div>

        <div className="border rounded-md px-3 py-2 mb-3 text-sm" style={{ borderColor: "#fecaca", background: "rgba(220,38,38,0.05)" }}>
          <div className="font-medium text-page-text mb-1">What gets deleted</div>
          <ul className="list-disc pl-5 space-y-0.5 text-page-text">
            <li>The running app — its namespace <span className="font-mono text-xs">{deployment?.namespace || "—"}</span>, every pod and service, and its load balancer.</li>
            <li>Its published addresses: <span className="font-mono text-xs">{deployment?.domain || "—"}</span> and every app/subdomain address of this deployment stop resolving.</li>
            <li>Its container images in the registry.</li>
          </ul>
          <div className="font-medium text-page-text mt-2 mb-1">What is kept</div>
          <ul className="list-disc pl-5 space-y-0.5 text-page-text">
            <li>The deployment history — this deployment stays listed, marked shut down.</li>
            <li>The project itself, its data and its apps inside cortex. Only the standalone app goes.</li>
            <li>The TLS certificates, so a later deployment comes back on HTTPS at once.</li>
          </ul>
        </div>

        <div className="mb-4">
          <div className={sectionLabel + " mb-1.5"}>Type the project name to confirm</div>
          <input value={typed} data-testid="deployment-teardown-input" autoFocus
            placeholder={projectSlug}
            onChange={(e) => setTyped(e.target.value)}
            className={fieldCls + " w-full font-mono"} />
          <div className="text-xs text-page-muted mt-1">
            Type <span className="font-mono">{projectSlug}</span> exactly.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost} data-testid="deployment-teardown-cancel">Cancel</button>
          <button type="button" disabled={!armed || busy} data-testid="deployment-teardown-confirm"
            onClick={() => onConfirm(typed.trim())}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white text-sm px-3 py-1.5 border-0 cursor-pointer hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-default">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PowerOff size={14} />} Shut the app down
          </button>
        </div>
      </div>
    </div>
  );
}

// Detail pane for one deployment: what shipped, which images (immutable tags),
// and — for the currently live one — component health.
function DeploymentDetail({ d, isCurrent, components, onRedeploy, onTeardown, busy, tearingDown, msg, favicons, onRegenerateFavicon, onSetAppTitle, faviconBusy }) {
  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="deployment-detail">
      <div>
        <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
          {d.kind === "teardown" ? <PowerOff size={20} /> : <Rocket size={20} />}
          {d.kind === "teardown" ? "Teardown" : `Deployment ${d.seq ?? "—"}`}
          {isCurrent ? <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: "var(--pill-green-strong-bg,#dcfce7)", color: "var(--pill-green-strong-color,#166534)" }}>current</span> : null}
        </h1>
        <div className="text-sm text-page-muted mt-1">
          {fmt(d.startedAt)} · <StatusWord status={d.status} />
          {d.finishedAt ? ` · finished ${fmt(d.finishedAt)}` : ""}
          {" · commit "}<span className="font-mono">{shortHash(d.commitHash)}</span>
          {" · tag "}<span className="font-mono">{d.tag || "—"}</span>
        </div>
        {d.error ? <div className="text-sm mt-2" style={{ color: "#dc2626" }}>{d.error}</div> : null}
        {/* Action feedback (a teardown result, or why one was refused) belongs
            HERE as well as on the overview: the actions that produce it are on
            this pane, and a message the user has to navigate away to read is a
            message they never see. */}
        {msg ? <div className="text-xs text-page-muted mt-2" data-testid="deployment-detail-msg">{msg}</div> : null}
      </div>

      <div className="flex items-center gap-2">
        {d.url && d.status === "live" ? (
          <a href={d.url} target="_blank" rel="noreferrer" data-testid="deployment-open-app"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-nav-active text-white no-underline hover:opacity-90">
            <ExternalLink size={14} /> Open app
          </a>
        ) : null}
        {d.commitHash && d.status !== "running" && !isCurrent ? (
          <button onClick={() => onRedeploy(d)} disabled={busy} data-testid={`deployment-redeploy-${d.seq}`}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-page-bg disabled:opacity-50">
            <History size={14} /> Redeploy this version
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-page-divider p-3">
          <div className="text-xs text-page-muted inline-flex items-center gap-1"><Globe size={11} /> Domain</div>
          <div className="text-sm mt-1 break-all">{d.domain || "—"}</div>
        </div>
        <div className="rounded-lg border border-page-divider p-3">
          <div className="text-xs text-page-muted">Namespace</div>
          <div className="text-sm mt-1 font-mono break-all">{d.namespace || "—"}</div>
        </div>
      </div>

      {/* the settings this deployment shipped with (chosen in the deploy
          modal, recorded on the engine's history row). Pre-modal rows carry
          no options — shown as the defaults they were deployed with. */}
      <div>
        <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Settings2 size={14} /> Settings</h2>
        <div className="border border-page-divider rounded-md px-3 py-1" data-testid="deployment-settings">
          <div className="flex items-center gap-2 text-sm py-1.5">
            <span className="font-medium w-56 shrink-0 truncate">Access</span>
            {d.options?.authEnabled === false ? (
              <span className="inline-flex items-center gap-1.5" data-testid="deployment-settings-auth">
                <LockOpen size={13} style={{ color: "#d97706" }} /> Public — no sign-in on the app
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5" data-testid="deployment-settings-auth">
                <Lock size={13} style={{ color: "#16a34a" }} /> Sign-in required{d.options ? "" : " (default)"}
              </span>
            )}
          </div>
          {/* what this deployment did to its own data. Rows from before the
              option existed carry no seedData — they were all full overwrites,
              which is what "(default)" said then; say so rather than claim the
              new default was in force. */}
          <div className="flex items-center gap-2 text-sm py-1.5 border-t border-page-divider">
            <span className="font-medium w-56 shrink-0 truncate">Data</span>
            {d.options?.seedData === true ? (
              <span className="inline-flex items-center gap-1.5" data-testid="deployment-settings-seed">
                <RefreshCw size={13} style={{ color: "#d97706" }} /> Reset from cortex — data overwritten
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5" data-testid="deployment-settings-seed">
                <Database size={13} style={{ color: "#16a34a" }} /> Kept the deployment's data
                {d.options?.seedData === false ? "" : " (not recorded)"}
              </span>
            )}
          </div>
          <div className="flex items-start gap-2 text-sm py-1.5 border-t border-page-divider">
            <span className="font-medium w-56 shrink-0 truncate">Custom subdomains</span>
            {(d.options?.subdomains || []).length === 0 ? (
              <span className="text-page-muted">none — generated domains only</span>
            ) : (
              <span className="flex flex-col gap-1 min-w-0">
                {(d.options?.subdomains || []).map((s) => (
                  <span key={s.name} className="inline-flex items-center gap-1.5 min-w-0" data-testid={`deployment-settings-subdomain-${s.name}`}>
                    {s.url && d.status === "live" ? (
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-nav-active hover:underline inline-flex items-center gap-1 truncate font-mono">
                        {s.domain || `${s.name}.tiva-ai.com`} <ExternalLink size={11} className="shrink-0" />
                      </a>
                    ) : (
                      <span className="font-mono truncate">{s.domain || `${s.name}.tiva-ai.com`}</span>
                    )}
                    {s.module ? <span className="text-xs text-page-muted shrink-0">→ {s.module}</span> : null}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* what each app's SHARE page offered when this deployment shipped */}
      <ViewerOptionsSection apps={d.options?.viewerOptions} />

      {/* every published site of this deployment — real clickable links, and
          (for the app views) each app's browser-tab identity: the generated
          favicon + the humanized title the page carries. This is where it
          belongs rather than in the Viewer options block above: this section
          already has one row per APP, and Viewer options is a frozen
          historical snapshot with nothing to click by contract. */}
      <div>
        <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Globe size={14} /> Published links</h2>
        <div className="border border-page-divider rounded-md px-3 py-1" data-testid="deployment-links">
          <div className="flex items-center gap-2 text-sm py-1.5">
            <span className="font-medium w-56 shrink-0 truncate">{d.flowUrl ? "Portal" : "Dashboard"}</span>
            {d.url ? (
              <a href={d.url} target="_blank" rel="noreferrer" className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                {d.domain} <ExternalLink size={12} className="shrink-0" />
              </a>
            ) : <span className="text-page-muted">not published</span>}
          </div>
          {d.flowUrl ? (
            <div className="flex items-center gap-2 text-sm py-1.5 border-t border-page-divider">
              <span className="font-medium w-56 shrink-0 truncate">Flow (endpoints dashboard)</span>
              <a href={d.flowUrl} target="_blank" rel="noreferrer" className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                {String(d.flowUrl).replace(/^https?:\/\//, "")} <ExternalLink size={12} className="shrink-0" />
              </a>
            </div>
          ) : null}
          {(d.views || []).map((v) => (
            <div key={v.module} className="flex items-center gap-2 text-sm py-1.5 border-t border-page-divider"
              data-testid={`deployment-app-row-${v.module}`}>
              <span className="font-medium w-56 shrink-0 truncate inline-flex items-center gap-2">
                <FaviconPreview module={v.module} svg={favicons?.[v.module]?.svg} />
                {onSetAppTitle ? (
                  <EditableAppTitle module={v.module} busy={faviconBusy === v.module}
                    title={favicons?.[v.module]?.title || humanTitle(v.module)} onSave={onSetAppTitle} />
                ) : (
                  <span className="truncate" title={v.module}>{favicons?.[v.module]?.title || humanTitle(v.module)}</span>
                )}
              </span>
              {v.url ? (
                <a href={v.url} target="_blank" rel="noreferrer" data-testid={`deployment-view-link-${v.module}`}
                  className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                  {v.domain} <ExternalLink size={12} className="shrink-0" />
                </a>
              ) : <span className="text-page-muted truncate">{v.domain} (pending)</span>}
              {onRegenerateFavicon ? (
                <span className="ml-auto shrink-0">
                  <RegenerateFaviconButton module={v.module}
                    busy={faviconBusy === v.module} onRegenerate={onRegenerateFavicon} />
                </span>
              ) : null}
            </div>
          ))}
          {(d.views || []).length === 0 ? (
            <div className="text-xs text-page-muted py-1.5 border-t border-page-divider">No app views in this deployment.</div>
          ) : null}
          {(d.views || []).length ? (
            <div className="text-[11px] text-page-muted py-1.5 border-t border-page-divider" data-testid="deployment-favicon-hint">
              The icon and title are baked into each app's page at build time — a regenerated icon applies on next deploy.
            </div>
          ) : null}
        </div>
      </div>

      {/* images shipped in this deployment — the immutable per-deploy history */}
      <div>
        <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Package size={14} /> Images</h2>
        {(d.images || []).length === 0 ? <div className="text-sm text-page-muted">No image list recorded.</div> : (
          <div className="border border-page-divider rounded-md px-3 py-1">
            {(d.images || []).map((img, i) => (
              <div key={i} className={"flex items-center gap-2 text-xs py-1.5 " + (i ? "border-t border-page-divider" : "")}>
                <span className="font-medium w-32 shrink-0">{img.name}</span>
                <span className="font-mono text-page-muted truncate flex-1">{img.repo}</span>
                <span className="font-mono shrink-0">:{img.tag}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* live component health, shown for the current deployment */}
      {isCurrent && components?.length ? (
        <div>
          <h2 className="text-sm font-semibold text-page-title mb-2">Components</h2>
          <div className="border border-page-divider rounded-md px-3 py-1">
            {components.map((c, i) => (
              <div key={c.name} className={"flex items-center gap-2 text-xs py-1.5 " + (i ? "border-t border-page-divider" : "")}>
                <StatusIcon size={13} status={c.deploy?.status === "running" ? "live" : c.deploy?.deployed ? "running" : "failed"} />
                <span className="font-medium w-40 shrink-0 truncate">{c.name}</span>
                <span className="text-page-muted w-28 shrink-0">{c.type}</span>
                <span className="text-page-muted flex-1">{c.deploy?.deployed ? `${c.deploy.ready}/${c.deploy.replicas} ready` : "not deployed"}</span>
                <span className="text-page-muted shrink-0">{c.image?.exists ? (c.image.publicImage ? "public image" : "image pushed") : "image missing"}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* DANGER ZONE — last thing on the page, deliberately (you scroll past
          everything this deployment IS before you get the button that ends
          it). Only the CURRENT live deployment can be torn down: there is one
          live app per project, and an older or already-shut-down row has
          nothing of its own to shut down. */}
      {isCurrent && d.status === "live" ? (
        <div className="pt-2" data-testid="deployment-danger-zone">
          <h2 className="text-sm font-semibold mb-2 inline-flex items-center gap-1.5" style={{ color: "#b91c1c" }}>
            <AlertTriangle size={14} /> Danger zone
          </h2>
          <div className="rounded-md border px-4 py-3 flex items-start gap-4"
            style={{ borderColor: "#fecaca", background: "rgba(220,38,38,0.04)" }}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-page-text">Teardown deployment</div>
              <div className="text-xs text-page-muted mt-0.5">
                Shuts the live app down: its namespace, load balancer, published addresses and
                container images are deleted. The deployment history is kept — this deployment
                stays listed, marked shut down. Deploy again to bring the app back.
              </div>
            </div>
            <button type="button" onClick={onTeardown} disabled={busy || tearingDown}
              data-testid="deployment-teardown-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-default"
              style={{ borderColor: "#fecaca", color: "#b91c1c", background: "transparent" }}>
              {tearingDown ? <Loader2 size={14} className="animate-spin" /> : <PowerOff size={14} />}
              {tearingDown ? "Shutting down…" : "Teardown deployment"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// DELIVERY — what an export ships to a customer's own infrastructure, edited
// here and stored on the fork as exportConfig.delivery (engine
// exporter/delivery.ts consumes it on every export / archive). The list of
// app services is READ from the engine so the person sees what will ship
// before pressing Deploy; the options are saved through the backend's strict
// validator (/api/deployments/delivery/save), which merges them into the
// export config without touching the git remote or the credentials.
// The viewer companions a customer archive can carry (dial order) — the keys of
// delivery.distribute. Labels are the customer-facing words; the hint says what
// ticking one adds to the archive.
const COMPANION_OPTIONS = [
  ["chat", "Chat", "the agent runtime (needs a Claude credential the customer supplies)"],
  ["workflows", "Workflows", "the workflow engine with its own stores + the Workflows pages"],
  ["guides", "Guides", "the guided tours, as pages"],
  ["models", "Models", "the model lab + the ML service"],
  ["pipeline", "Pipeline", "the pipeline pages + engine"],
  ["governance", "Governance", "the governance pages + engine"],
  ["docs", "Docs", "the requirements pages + engine"],
];
const distributeOf = (delivery) => Object.fromEntries(COMPANION_OPTIONS.map(([c]) => [c, !!(delivery?.distribute && delivery.distribute[c] === true)]));

function DeliverySection({ projectSlug, info, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!info?.delivery) return;
    setForm({
      appServices: info.delivery.appServices !== false,
      kubernetes: !!info.delivery.targets?.kubernetes,
      azureContainerApps: !!info.delivery.targets?.azureContainerApps,
      ci: ["github-actions", "azure-pipelines"].includes(info.delivery.ci) ? info.delivery.ci : "none",
      registry: info.delivery.registry || "",
      runbook: info.delivery.runbook !== false,
      devServices: Array.isArray(info.delivery.devServices) ? [...info.delivery.devServices].sort() : [],
      distribute: distributeOf(info.delivery),
    });
  }, [info]);
  if (!info || !form) return null;
  const appServices = info.appServices || [];
  const dirty = JSON.stringify(form) !== JSON.stringify({
    appServices: info.delivery.appServices !== false,
    kubernetes: !!info.delivery.targets?.kubernetes,
    azureContainerApps: !!info.delivery.targets?.azureContainerApps,
    ci: ["github-actions", "azure-pipelines"].includes(info.delivery.ci) ? info.delivery.ci : "none",
    registry: info.delivery.registry || "",
    runbook: info.delivery.runbook !== false,
    devServices: Array.isArray(info.delivery.devServices) ? [...info.delivery.devServices].sort() : [],
    distribute: distributeOf(info.delivery),
  });
  const toggleDev = (name, on) => setForm((f) => ({
    ...f, devServices: [...new Set(on ? [...f.devServices, name] : f.devServices.filter((n) => n !== name))].sort(),
  }));
  const save = async () => {
    setSaving(true); setError(""); setNote("");
    try {
      const r = await api("delivery/save", {
        target: projectSlug,
        delivery: {
          appServices: form.appServices,
          targets: { compose: true, kubernetes: form.kubernetes, azureContainerApps: form.azureContainerApps },
          ci: form.ci,
          registry: form.registry,
          runbook: form.runbook,
          devServices: form.devServices,
          distribute: form.distribute,
        },
      });
      setNote("Saved — the next export, deployment or archive ships with these options.");
      onSaved?.(r);
    } catch (e) {
      setError(String(e?.message || "Could not save the delivery options."));
    } finally { setSaving(false); }
  };
  const Check = ({ k, label, hint, disabled, testid }) => (
    <label className={"flex items-start gap-2 text-sm py-1 " + (disabled ? "opacity-60" : "cursor-pointer")}>
      <input type="checkbox" checked={!!form[k]} disabled={disabled} data-testid={testid}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked }))} className="mt-0.5 cursor-pointer" />
      <span><span className="text-page-text">{label}</span>{hint ? <span className="text-xs text-page-muted"> — {hint}</span> : null}</span>
    </label>
  );
  return (
    <div data-testid="deployments-delivery">
      <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Boxes size={14} /> Delivery</h2>
      <div className="text-xs text-page-muted mb-2">
        What every export of this project ships to a customer's own infrastructure — in the git repository, the
        cluster deployment and the local-run archive alike: the project's own runtimes, the deployment descriptors,
        a CI workflow, and a runbook generated from the same definitions. Current: <span data-testid="delivery-line">{info.line}</span>.
      </div>
      <div className="border border-page-divider rounded-md px-3 py-2 grid md:grid-cols-2 gap-4">
        <div>
          <div className={sectionLabel + " mb-1"}>What ships</div>
          <Check k="appServices" testid="delivery-opt-appServices" label="App services (source + Dockerfile, verbatim)"
            hint={appServices.length ? `${appServices.length} on this project` : "none on this project yet"} />
          {appServices.length ? (
            <div className="ml-6 mt-1 mb-2 border border-page-divider rounded-md px-2 py-1" data-testid="delivery-app-services">
              {appServices.map((s, i) => (
                <div key={s.exportName} data-testid={`delivery-app-service-${s.exportName}`}
                  className={"flex items-center gap-2 text-xs py-1 " + (i ? "border-t border-page-divider" : "")}>
                  <Server size={12} className="shrink-0 text-page-muted" />
                  <span className="font-mono shrink-0" title={s.name}>{s.name}</span>
                  <span className="text-page-muted truncate min-w-0">
                    {s.tcp ? `TCP ${s.port}` : `HTTP ${s.port} · ${s.healthPath}`}{s.route ? ` · ${s.route}` : ""} · {s.files} file{s.files === 1 ? "" : "s"}
                    {!s.hasDockerfile ? " · no Dockerfile" : ""}
                  </span>
                  {/* A DEVELOPMENT STAND-IN: a sample database the customer
                      replaces with their own. In a customer archive it moves to
                      docker-compose.dev.yml and out of the k8s / ACA descriptors;
                      the variables that name it are left for the customer. */}
                  <label className="ml-auto inline-flex items-center gap-1 text-page-muted cursor-pointer shrink-0"
                    title="Ship as a development stand-in: docker-compose.dev.yml only, not in the deployment descriptors; the customer points the other services at their own system">
                    <input type="checkbox" checked={form.devServices.includes(s.name)} data-testid={`delivery-dev-${s.exportName}`}
                      onChange={(e) => toggleDev(s.name, e.target.checked)} className="cursor-pointer" />
                    dev stand-in
                  </label>
                  <span className="text-page-muted shrink-0">{s.status || ""}</span>
                </div>
              ))}
            </div>
          ) : null}
          <Check k="runbook" testid="delivery-opt-runbook" label="Runbook + README"
            hint="docs/RUNBOOK.md: setup, deployment, health, support, rollback — generated" />
        </div>
        <div>
          <div className={sectionLabel + " mb-1"}>Deployment targets</div>
          <label className="flex items-start gap-2 text-sm py-1 opacity-60">
            <input type="checkbox" checked readOnly disabled className="mt-0.5" data-testid="delivery-opt-compose" />
            <span><span className="text-page-text">Docker Compose</span><span className="text-xs text-page-muted"> — always; the archive's own format</span></span>
          </label>
          <Check k="kubernetes" testid="delivery-opt-kubernetes" label="Kubernetes manifests" hint="infra/kubernetes: Deployment, Service, probes, Ingress, kustomization" />
          <Check k="azureContainerApps" testid="delivery-opt-aca" label="Azure Container Apps" hint="infra/azure-container-apps: Bicep + deploy.sh" />
          <div className={sectionLabel + " mt-3 mb-1"}>Continuous delivery</div>
          <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
            <input type="checkbox" checked={form.ci === "github-actions"} data-testid="delivery-opt-ci"
              onChange={(e) => setForm((f) => ({ ...f, ci: e.target.checked ? "github-actions" : "none" }))} className="mt-0.5 cursor-pointer" />
            <span><span className="text-page-text">GitHub Actions workflow</span><span className="text-xs text-page-muted"> — builds every image on push, pushes to the registry, deploys when secrets exist</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm py-1 cursor-pointer">
            <input type="checkbox" checked={form.ci === "azure-pipelines"} data-testid="delivery-opt-ci-azure"
              onChange={(e) => setForm((f) => ({ ...f, ci: e.target.checked ? "azure-pipelines" : "none" }))} className="mt-0.5 cursor-pointer" />
            <span><span className="text-page-text">Azure Pipelines</span><span className="text-xs text-page-muted"> — azure-pipelines.yml: Docker@2 build-and-push per image, deploy stages gated on service connections (one CI at a time)</span></span>
          </label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-page-muted w-16 shrink-0">Registry</span>
            <input value={form.registry} data-testid="delivery-registry" placeholder="ghcr.io/<org> · <name>.azurecr.io"
              onChange={(e) => setForm((f) => ({ ...f, registry: e.target.value }))} className={fieldCls + " flex-1 text-xs font-mono"} />
          </div>
        </div>
        {/* WHAT THE CUSTOMER RECEIVES of cortex's own features — the customer
            archive only. A companion left unticked is not in the archive even
            when an app's Viewer options show it here on the platform: it leaves
            the dial the app resolves, and the engine, the operations pages and
            the agent runtime ship only when a ticked companion needs them. All
            off by default: the customer gets exactly the application. */}
        <div className="md:col-span-2 pt-1 border-t border-page-divider" data-testid="delivery-distribute">
          <div className={sectionLabel + " mb-1"}>What the customer receives (customer archive)</div>
          <div className="text-xs text-page-muted mb-1">
            cortex features the delivered apps use on this platform ship to the customer only when ticked here. Off = the archive
            carries the application alone: no workflow engine, no operations pages, no agent runtime, and the app's side menu
            offers none of them.
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-x-4">
            {COMPANION_OPTIONS.map(([c, label, hint]) => (
              <label key={c} className="flex items-start gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={!!form.distribute[c]} data-testid={`delivery-dist-${c}`}
                  onChange={(e) => setForm((f) => ({ ...f, distribute: { ...f.distribute, [c]: e.target.checked } }))} className="mt-0.5 cursor-pointer" />
                <span><span className="text-page-text">{label}</span><span className="text-xs text-page-muted"> — {hint}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2 flex items-center gap-3 pt-1 border-t border-page-divider">
          <button type="button" disabled={!dirty || saving} data-testid="delivery-save" onClick={save} className={btnPrimary}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save delivery options
          </button>
          {note ? <span className="text-xs text-page-muted" data-testid="delivery-saved-note">{note}</span> : null}
          {error ? <span className="text-xs" style={{ color: "#dc2626" }} data-testid="delivery-error">{error}</span> : null}
          {info.gitUrl ? <span className="ml-auto text-xs text-page-muted truncate" title={info.gitUrl}>exports to {info.gitUrl.replace(/^https?:\/\//, "")}</span> : null}
        </div>
      </div>
    </div>
  );
}

export default function Deployments() {
  const { projectSlug } = useProject();
  const [deployments, setDeployments] = useState(null);
  const [current, setCurrent] = useState(null);   // config doc: the live deployment
  const [status, setStatus] = useState(null);      // GET_DEPLOY_STATUS merge
  const [dashboard, setDashboard] = useState(null); // standalone dashboard credentials
  const [modules, setModules] = useState([]);      // built view modules (deploy modal's app select)
  // Per-app favicons, as { <module>: { module, title, svg, salt } }. CURRENT
  // config (not per-deployment history): the stored icon is what the next
  // deploy will bake, which is exactly what the regenerate control edits.
  const [favicons, setFavicons] = useState({});
  const [faviconBusy, setFaviconBusy] = useState("");
  // Delivery options + what ships (engine GET_DELIVERY_PREVIEW via the backend).
  // Failure-tolerant like the favicons: an engine that cannot answer hides the
  // section rather than breaking the page.
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [sel, setSel] = useState(null);            // selected deployment _id
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem("deploymentsRailOpen") !== "0"; } catch { return true; }
  });
  const toggleRail = () => setRailOpen((o) => { const n = !o; try { localStorage.setItem("deploymentsRailOpen", n ? "1" : "0"); } catch {} return n; });

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const r = await api("list", { target: projectSlug });
      setDeployments(r?.deployments || []);
      setCurrent(r?.current || null);
      setStatus(r?.status || null);
      setDashboard(r?.dashboard || null);
      // Additive field: an older backend (or a hiccuping build-status RPC)
      // simply leaves the list empty and the modal falls back to history.
      setModules(Array.isArray(r?.modules) ? r.modules : []);
      // Additive + failure-tolerant, same as `modules`: no favicons recorded
      // (or an engine that could not answer) just means no previews.
      setFavicons(Object.fromEntries((Array.isArray(r?.favicons) ? r.favicons : [])
        .filter((f) => f && typeof f.module === "string")
        .map((f) => [f.module, f])));
      api("delivery/get", { target: projectSlug }).then((d) => setDeliveryInfo(d?.ok ? d : null)).catch(() => setDeliveryInfo(null));
    } catch (e) {
      setErr(String(e?.message || "The deployment service is not reachable."));
      setDeployments((p) => p ?? []);
    }
  }, [projectSlug]);
  useEffect(() => { setSel(null); refresh(); }, [refresh]);

  // OPTIMISTIC in-progress row. /api/deployments/run is not a fire-and-forget
  // call: the backend runs EXPORT_PROJECT synchronously (tens of seconds on a
  // big project) before the background deploy starts, and the engine's
  // "running" history row therefore only exists AFTER that export. Between the
  // click and that row there is nothing to show — which read as "nothing
  // happened". So the click records a local pending row {at} and the rail
  // renders it until a server row supersedes it.
  const [pending, setPending] = useState(null);

  const serverRunning = (deployments || []).some((d) => d.status === "running");

  // Reconciliation: the pending row is cleared as soon as /list returns a row
  // that COVERS it — either a genuinely "running" row, or (for a deploy that
  // already sailed past running by the time we polled) any row started at or
  // after the click. A refresh that simply comes back without such a row is
  // NOT evidence of failure: the row is expected to be 10–60s late, so the
  // pending row must survive those empty refreshes.
  useEffect(() => {
    if (!pending || deployments == null) return;
    const covered = (deployments || []).some((d) =>
      d.status === "running" || (d.startedAt && new Date(d.startedAt).getTime() >= pending.at - 1000));
    if (covered) setPending(null);
  }, [deployments, pending]);

  // Poll while ANYTHING is in flight — including a pending row that no server
  // row backs yet, which is exactly the window the poll used to skip.
  const anyRunning = serverRunning || !!pending;
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [anyRunning, refresh]);

  // "Deploy project" first opens the settings modal; the actual deploy call
  // happens on confirm, carrying the chosen options.
  const [showDeployModal, setShowDeployModal] = useState(false);

  // Confirm closes the modal SYNCHRONOUSLY and lets the request finish in the
  // background — the user must never sit in front of a spinning modal for the
  // length of the export. The pending row is what tells them it started.
  const deployNow = (options) => {
    setShowDeployModal(false);
    setMsg("");
    const at = Date.now();
    setPending({ at });
    api("run", { target: projectSlug, options })
      .then((r) => {
        setMsg(`Deployment started (commit ${shortHash(r?.commitHash)}) — images build in the cluster; this takes several minutes. Progress updates here.`);
        refresh();
      })
      .catch((e) => {
        // The deploy never started: drop the optimistic row so the rail does
        // not lie about work in flight, and surface why.
        setPending((p) => (p && p.at === at ? null : p));
        setMsg(String(e?.message || "Failed to start the deployment."));
      });
  };

  // ── Local-run bundles ─────────────────────────────────────────────────────
  // The other destination the modal offers: don't deploy, package. Same
  // fire-and-poll shape as a deploy (the export alone is tens of seconds), with
  // its own optimistic row so the click is visibly acknowledged, and its own
  // status list because the bundles are engine-side rows keyed by `kind`.
  const [packages, setPackages] = useState([]);
  const [packagePending, setPackagePending] = useState(null);
  const [downloading, setDownloading] = useState("");

  const refreshPackages = useCallback(async () => {
    try {
      const r = await api("package/status", { target: projectSlug });
      setPackages(Array.isArray(r?.packages) ? r.packages : []);
    } catch {
      // Additive feature: an older backend has no such route. Leaving the list
      // empty is the honest reading and keeps the rest of the page working.
      setPackages([]);
    }
  }, [projectSlug]);
  useEffect(() => { refreshPackages(); }, [refreshPackages]);

  const packageRunning = packages.some((p) => p.status === "running") || !!packagePending;
  useEffect(() => {
    if (!packageRunning) return;
    const t = setInterval(refreshPackages, 5000);
    return () => clearInterval(t);
  }, [packageRunning, refreshPackages]);

  // Clear the optimistic row once a server row covers it. Reconciled by COUNT,
  // not by timestamp: `startedAt` is stamped by the engine, whose clock and
  // timezone need not agree with the browser's (locally the exporter container
  // runs eight hours behind the host), so a "started at or after the click"
  // test can never fire and the row sticks forever. The count at click time is
  // an unambiguous local fact — one more row than we had is our row.
  useEffect(() => {
    if (!packagePending) return;
    if (packages.length > packagePending.count || packages.some((p) => p.status === "running")) {
      setPackagePending(null);
    }
  }, [packages, packagePending]);

  const packageNow = (options) => {
    setShowDeployModal(false);
    setMsg("");
    const at = Date.now();
    setPackagePending({ at, count: packages.length });
    api("package", { target: projectSlug, options })
      .then((r) => {
        setMsg(`Building the local-run archive (commit ${shortHash(r?.commitHash)}) — the images' build contexts and this project's data are packaged. It appears below when it is ready.`);
        refreshPackages();
      })
      .catch((e) => {
        setPackagePending((p) => (p && p.at === at ? null : p));
        setMsg(String(e?.message || "Failed to start packaging."));
      });
  };

  // Download the archive. Goes through fetchData (so it carries the session
  // token — the route is auth-gated like every other) and lands as a blob the
  // browser saves, rather than a bare <a href> a plain navigation could not
  // authenticate.
  const downloadBundle = async (p) => {
    if (!p?.bundleId) return;
    setDownloading(p.bundleId); setMsg("");
    try {
      const qs = `target=${encodeURIComponent(projectSlug)}&bundle=${encodeURIComponent(p.bundleId)}`;
      const blob = await fetchData(`deployments/package/download?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = p.fileName || `${projectSlug}-local.tar.gz`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`Downloaded ${p.fileName || "the archive"} — unpack it and run ./run.sh.`);
    } catch (e) {
      setMsg(String(e?.message || "Could not download the archive."));
      refreshPackages();
    } finally { setDownloading(""); }
  };

  // REGENERATE one app's favicon. Non-destructive — the only thing it changes
  // is the stored icon for that module, which the app's NEXT deploy bakes into
  // its page. No dialog for that reason; the preview updates in place from the
  // SVG the engine returns, and the message states when it takes effect.
  const regenerateFavicon = async (mod) => {
    setFaviconBusy(mod); setMsg("");
    try {
      const r = await api("favicon/regenerate", { target: projectSlug, module: mod });
      if (r?.svg) {
        setFavicons((p) => ({ ...p, [mod]: { module: mod, title: r.title || humanTitle(mod), svg: r.svg, salt: r.salt } }));
        setMsg(`New icon for ${r.title || humanTitle(mod)} — it applies on the app's next deploy.`);
      } else {
        setMsg("The engine did not return a new icon.");
      }
    } catch (e) {
      setMsg(String(e?.message || "Could not regenerate the icon."));
    } finally {
      setFaviconBusy("");
    }
  };

  // Rename one app: stores the display title on the engine (empty = back to
  // the auto title) and redraws the favicon from the new initials. Baked at
  // build time like the icon — applies on the app's next deploy.
  const setAppTitle = async (mod, title) => {
    setFaviconBusy(mod); setMsg("");
    try {
      const r = await api("app_title", { target: projectSlug, module: mod, title });
      if (r?.module) {
        setFavicons((p) => ({ ...p, [mod]: { module: mod, title: r.title || humanTitle(mod), customTitle: !!r.customTitle, svg: r.svg, salt: r.salt } }));
        setMsg(`"${r.title || humanTitle(mod)}" — the new title and icon apply on the app's next deploy.`);
      } else {
        setMsg("The engine did not accept the title.");
      }
    } catch (e) {
      setMsg(String(e?.message || "Could not save the title."));
    } finally {
      setFaviconBusy("");
    }
  };

  // TEARDOWN of the live app. Destructive, so it goes through the typed-name
  // dialog; the backend re-validates the typed name and is authoritative.
  const [teardownFor, setTeardownFor] = useState(null); // the deployment being torn down
  const [tearingDown, setTearingDown] = useState(false);

  const teardownNow = async (confirm) => {
    setTearingDown(true); setMsg("Shutting the app down — removing its addresses, namespace and images…");
    setTeardownFor(null);
    try {
      const r = await api("teardown", { target: projectSlug, confirm });
      const parts = [
        `Deployment shut down (namespace ${r?.namespace || "—"})`,
        `${r?.domainsRemoved ?? 0} address(es) removed`,
        `${r?.ecrReposRemoved ?? 0} image repo(s) deleted`,
      ];
      if (r?.namespaceState === "terminating") parts.push("the namespace is still terminating in the cluster");
      if (r?.errors?.length) parts.push(`issues: ${r.errors.join("; ")}`);
      setMsg(parts.join(" · ") + ".");
      setSel(null);
      await refresh();
    } catch (e) {
      // A FAILED teardown (the backend answers 502 when the engine reports
      // success:false — the namespace survived, so the app is still serving).
      // Stay on the deployment and say why: the server never stamped anything,
      // so a refresh brings back the same live row, with its Danger zone, and
      // the user can retry. Navigating to the overview here — or worse, into a
      // "not deployed" reading — would be a lie about a running app.
      setMsg(String(e?.message || "Failed to shut the deployment down.") +
        " The app is still deployed — nothing was shut down.");
      await refresh();
    } finally { setTearingDown(false); }
  };

  const redeploy = async (d) => {
    setBusy(true); setMsg("");
    try {
      // A rollback reuses the settings recorded on that deployment's row
      // (older rows carry none → server defaults: auth on, no subdomains).
      const options = d.options ? {
        authEnabled: d.options.authEnabled !== false,
        subdomains: (d.options.subdomains || []).map((s) => (s.module ? { name: s.name, module: s.module } : { name: s.name })),
      } : undefined;
      await api("redeploy", { target: projectSlug, commitHash: d.commitHash, options });
      setMsg(`Redeploying commit ${shortHash(d.commitHash)} — its images are rebuilt under the same tag and rolled out.`);
      setSel(null);
      setTimeout(refresh, 4000);
    } catch (e) { setMsg(String(e?.message || "Failed to start the redeploy.")); }
    finally { setBusy(false); }
  };

  // Modal prefills: the most recent deployment that recorded options; app
  // modules for the subdomain → app select.
  //
  // The module list is the project's BUILT view modules (from /list, which asks
  // the exporter) UNION whatever deployment history recorded. History alone was
  // the old source and it is empty in two real cases — a first-ever deploy, and
  // after a teardown (no `current`, and the newest row is the `{kind:"teardown"}`
  // audit row, which has no views) — which hid the selector on projects that do
  // have several apps, silently pointing every subdomain at the first module.
  //
  // Deliberately NOT derived from the app registry: an app's view_name does not
  // determine its module name, so guessing would feed the exporter names it
  // rejects (it falls back to the first sorted module) — a selector that lies.
  // If the union is empty the selector stays hidden, which is honest: a project
  // that has never been exported has no built modules for a subdomain to point
  // at yet, and its first export is what creates them.
  const lastOptions = (deployments || []).find((d) => d.options)?.options || null;
  const knownModules = [...new Set([
    ...(modules || []),
    ...((current?.views || []).map((v) => v.module)),
    ...(((deployments || [])[0]?.views || []).map((v) => v.module)),
  ])].filter(Boolean).sort();

  const selected = sel != null ? (deployments || []).find((d) => String(d._id) === String(sel)) : null;
  const isCurrent = (d) => !!(current && d && (current.tag ? current.tag === d.tag : current.commitHash === d.commitHash) && d.status === "live");

  const Section = ({ label, children }) => (
    <div className="mb-2">
      <div className="px-2 pt-2 pb-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-page-muted">{label}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="flex h-full min-h-0" data-testid="deployments-page">
      {!railOpen && (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button type="button" data-testid="deployments-rail-open" onClick={toggleRail} title="Show deployments"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftOpen size={18} />
          </button>
          <Rocket size={16} className="text-nav-active mt-1" />
        </div>
      )}

      {/* Left rail: deployment history (collapsible), mirroring Apps/Agents/Audit */}
      <aside className={(railOpen ? "w-72" : "hidden") + " shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0"}>
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <Rocket size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Deployments</span>
          <button type="button" data-testid="deployments-refresh" onClick={refresh} title="Refresh"
            className="ml-auto text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <RefreshCw size={14} />
          </button>
          <button type="button" data-testid="deployments-rail-close" onClick={toggleRail} title="Collapse deployments list"
            className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <PanelLeftClose size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <button type="button" data-testid="deployments-overview-row" onClick={() => setSel(null)}
            className={
              "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
              (sel == null ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
            }>
            <Gauge size={15} className="shrink-0" />
            <span className="min-w-0 flex flex-col">
              <span className="font-medium">Overview</span>
              <span className="text-xs text-page-muted">
                {current ? `live · ${current.domain}` : deployments == null ? "loading…" : "not deployed yet"}
              </span>
            </span>
          </button>

          <Section label="Deployments">
            {/* the optimistic row: not selectable (there is no server row to
                open yet), it exists purely so the rail shows work in flight
                from the instant Deploy is pressed. */}
            {pending ? (
              <div data-testid="deployment-optimistic"
                className="w-full text-left px-3 py-2 flex items-center gap-2 rounded-md text-sm text-page-text">
                <Loader2 size={15} className="shrink-0 animate-spin" style={{ color: "#d97706" }} />
                <span className="min-w-0 flex flex-col">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap font-medium">Deployment starting…</span>
                  <span className="text-xs text-page-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    just now · compiling the project
                  </span>
                </span>
              </div>
            ) : null}
            {deployments == null && (
              <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
                <Loader2 size={16} className="animate-spin" /> Loading deployments…
              </div>
            )}
            {deployments != null && deployments.length === 0 && !pending && (
              <div className="text-sm text-page-muted px-2 py-2">
                No deployments yet — ship the first one below.
              </div>
            )}
            {(deployments || []).map((d) => (
              <button key={String(d._id)} type="button"
                data-testid={d.kind === "teardown" ? `teardown-${d.seq}` : `deployment-${d.seq}`}
                onClick={() => setSel(String(d._id))}
                className={
                  "w-full text-left px-3 py-2 flex items-center gap-2 cursor-pointer border-0 rounded-md text-sm " +
                  (sel === String(d._id) ? "bg-nav-active/10 text-nav-active" : "bg-transparent text-page-text hover:bg-card-border/40")
                }>
                <StatusIcon status={d.kind === "teardown" ? "shut_down" : d.status} />
                <span className="min-w-0 flex flex-col">
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap font-medium">
                    {/* A teardown is an event in the history, not a version of
                        the app — labelling it "Deployment N" would read as one. */}
                    {d.kind === "teardown" ? "Teardown" : `Deployment ${d.seq ?? "—"}`}
                    {isCurrent(d) ? " · current" : ""}
                  </span>
                  <span className="text-xs text-page-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    {fmt(d.startedAt)} · {shortHash(d.commitHash)}
                  </span>
                </span>
              </button>
            ))}
          </Section>
        </div>

        {/* Rail footer: the primary action, mirroring "Build an agent". */}
        <div className="p-2 border-t border-card-border">
          {/* Deploy is disabled while a teardown is in flight: the two would
              race over the same namespace, addresses and image repos. */}
          <button type="button" data-testid="deployments-run-btn" onClick={() => setShowDeployModal(true)} disabled={anyRunning || tearingDown}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer disabled:opacity-50">
            {anyRunning || tearingDown ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {tearingDown ? "Shutting down…" : anyRunning ? "Deployment in progress…" : "Deploy project"}
          </button>
        </div>
      </aside>

      {/* Main pane: overview by default; a selected deployment's detail otherwise */}
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto bg-page-bg">
        {selected ? (
          <DeploymentDetail d={selected} isCurrent={isCurrent(selected)} components={status?.services || []}
            onRedeploy={redeploy} onTeardown={() => setTeardownFor(selected)} busy={busy} tearingDown={tearingDown} msg={msg}
            favicons={favicons} onRegenerateFavicon={regenerateFavicon} onSetAppTitle={setAppTitle} faviconBusy={faviconBusy} />
        ) : (
          <div className="p-6 max-w-4xl mx-auto space-y-5" data-testid="deployments-overview">
            <div>
              <h1 className="text-xl font-medium text-page-title inline-flex items-center gap-2">
                <Rocket size={20} /> Deployments
              </h1>
              <div className="text-sm text-page-muted mt-1">
                Ship <b>{projectSlug}</b> as a standalone app — its own infrastructure and domain,
                fully separate from the cortex platform. Every deployment compiles the project's
                implemented elements (endpoints, pipelines, workflows engine, apps, services,
                the endpoints dashboard) and keeps its own immutable image set.
              </div>
            </div>
            {msg ? <div className="text-xs text-page-muted">{msg}</div> : null}
            {err ? <div className="text-sm text-red-600">{err}</div> : null}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Live app</div>
                <div className="text-sm mt-1 break-all">
                  {current?.url ? (
                    <a href={current.url} target="_blank" rel="noreferrer" data-testid="deployments-live-link" className="text-nav-active hover:underline inline-flex items-center gap-1">
                      {current.domain} <ExternalLink size={12} />
                    </a>
                  ) : <span className="text-page-muted">not deployed</span>}
                </div>
                {dashboard?.login ? (
                  <div className="text-[11px] text-page-muted mt-1.5" data-testid="deployments-dashboard-creds">
                    dashboard login <span className="font-mono">{dashboard.login}</span> ·{" "}
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="bg-transparent border-0 p-0 cursor-pointer text-page-muted hover:text-page-text font-mono">
                      {showPw ? (dashboard.password || "—") : "••••••"}
                    </button>
                    <span className="ml-1">(editable in the Flow export panel)</span>
                  </div>
                ) : null}
              </div>
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Deployments</div>
                <div className="text-2xl font-semibold">{deployments == null ? "—" : deployments.length}</div>
              </div>
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Live version</div>
                <div className="text-sm mt-1 font-mono">{current ? `${shortHash(current.commitHash)} (:${current.tag || "latest"})` : "—"}</div>
              </div>
              <div className="rounded-lg border border-page-divider p-3">
                <div className="text-xs text-page-muted">Components ready</div>
                <div className="text-2xl font-semibold">
                  {status?.services?.length
                    ? `${status.services.filter((c) => c.deploy?.status === "running").length}/${status.services.length}`
                    : "—"}
                </div>
              </div>
            </div>

            <DeliverySection projectSlug={projectSlug} info={deliveryInfo}
              onSaved={(r) => setDeliveryInfo((d) => (d ? { ...d, delivery: r.delivery, line: r.line } : d))} />

            {/* published links for the LIVE deployment — clickable domains */}
            {current ? (
              <div>
                <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5"><Globe size={14} /> Published links</h2>
                <div className="border border-page-divider rounded-md px-3 py-1" data-testid="deployments-live-links">
                  <div className="flex items-center gap-2 text-sm py-1.5">
                    <span className="font-medium w-56 shrink-0 truncate">{current.flowUrl ? "Portal" : "Dashboard"}</span>
                    <a href={current.url} target="_blank" rel="noreferrer" className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                      {current.domain} <ExternalLink size={12} className="shrink-0" />
                    </a>
                  </div>
                  {current.flowUrl ? (
                    <div className="flex items-center gap-2 text-sm py-1.5 border-t border-page-divider">
                      <span className="font-medium w-56 shrink-0 truncate">Flow (endpoints dashboard)</span>
                      <a href={current.flowUrl} target="_blank" rel="noreferrer" className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                        {String(current.flowUrl).replace(/^https?:\/\//, "")} <ExternalLink size={12} className="shrink-0" />
                      </a>
                    </div>
                  ) : null}
                  {(current.views || []).map((v) => (
                    <div key={v.module} className="flex items-center gap-2 text-sm py-1.5 border-t border-page-divider">
                      {/* Icon + humanized title here too, so the overview shows
                          each app the way its browser tab does. The regenerate
                          control stays on the deployment detail pane. */}
                      <span className="font-medium w-56 shrink-0 truncate inline-flex items-center gap-2">
                        <FaviconPreview module={v.module} svg={favicons?.[v.module]?.svg} />
                        <span className="truncate" title={v.module}>{favicons?.[v.module]?.title || humanTitle(v.module)}</span>
                      </span>
                      <a href={v.url} target="_blank" rel="noreferrer" data-testid={`deployments-live-view-${v.module}`}
                        className="text-nav-active hover:underline inline-flex items-center gap-1 truncate">
                        {v.domain} <ExternalLink size={12} className="shrink-0" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Local-run archives. Shown whenever the project has any (or one
                is being built) — this is where the "Download for local run"
                choice in the deploy modal lands. */}
            {packages.length || packagePending ? (
              <div data-testid="deployments-packages">
                <h2 className="text-sm font-semibold text-page-title mb-2 inline-flex items-center gap-1.5">
                  <HardDriveDownload size={14} /> Local-run archives
                </h2>
                <div className="text-xs text-page-muted mb-2">
                  Each archive builds and runs this app on one machine with{" "}
                  <span className="font-mono">docker compose</span>. Unpack it and run{" "}
                  <span className="font-mono">./run.sh</span>. Archives are kept for a day.
                </div>
                <div className="border border-page-divider rounded-md px-3 py-1">
                  {packagePending ? (
                    <div className="flex items-center gap-2 text-sm py-2" data-testid="package-optimistic">
                      <Loader2 size={14} className="shrink-0 animate-spin" style={{ color: "#d97706" }} />
                      <span className="flex-1 min-w-0">Building the archive…</span>
                      <span className="text-xs text-page-muted">just now</span>
                    </div>
                  ) : null}
                  {packages.map((p, i) => (
                    <div key={p._id} data-testid={`package-${p.seq ?? i + 1}`}
                      className={"flex items-center gap-2 text-sm py-2 " + (i || packagePending ? "border-t border-page-divider" : "")}>
                      <StatusIcon size={14} status={p.status === "ready" ? "live" : p.status === "running" ? "running" : "failed"} />
                      <span className="min-w-0 flex-1 flex flex-col">
                        <span className="truncate">
                          {p.fileName || `archive ${p.seq ?? i + 1}`}
                          {p.sizeBytes ? <span className="text-page-muted"> · {(p.sizeBytes / 1024 / 1024).toFixed(1)} MB</span> : null}
                        </span>
                        <span className="text-xs text-page-muted truncate">
                          {fmt(p.startedAt)} · {shortHash(p.commitHash)}
                          {p.customer || p.options?.customer ? " · customer archive" : ""}
                          {p.components?.length ? ` · ${p.components.length} component${p.components.length === 1 ? "" : "s"}` : ""}
                          {p.status === "failed" && p.error ? ` · ${p.error}` : ""}
                          {p.status === "expired" ? " · expired — build it again" : ""}
                        </span>
                      </span>
                      {p.status === "ready" && p.available ? (
                        <button type="button" data-testid={`package-download-${p.seq ?? i + 1}`}
                          disabled={downloading === p.bundleId}
                          onClick={() => downloadBundle(p)} className={btnGhost}>
                          {downloading === p.bundleId
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Download size={13} />} Download
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {status?.services?.length ? (
              <div>
                <h2 className="text-sm font-semibold text-page-title mb-2">Live components</h2>
                <div className="border border-page-divider rounded-md px-3 py-1">
                  {status.services.map((c, i) => (
                    <div key={c.name} className={"flex items-center gap-2 text-xs py-1.5 " + (i ? "border-t border-page-divider" : "")}>
                      <StatusIcon size={13} status={c.deploy?.status === "running" ? "live" : c.deploy?.deployed ? "running" : "failed"} />
                      <span className="font-medium w-40 shrink-0 truncate">{c.name}</span>
                      <span className="text-page-muted w-28 shrink-0">{c.type}</span>
                      <span className="text-page-muted flex-1">{c.deploy?.deployed ? `${c.deploy.ready}/${c.deploy.replicas} ready` : "not deployed"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </main>

      {showDeployModal ? (
        <DeployOptionsModal
          defaults={lastOptions}
          knownModules={knownModules}
          busy={false}
          onClose={() => setShowDeployModal(false)}
          onDeploy={deployNow}
          onPackage={packageNow}
          deliveryLine={deliveryInfo?.line || ""}
        />
      ) : null}

      {teardownFor ? (
        <TeardownModal
          projectSlug={projectSlug}
          deployment={teardownFor}
          busy={tearingDown}
          onClose={() => setTeardownFor(null)}
          onConfirm={teardownNow}
        />
      ) : null}
    </div>
  );
}
